'use client';

import * as sx from '@stylexjs/stylex';
import Script from 'next/script';
import React, { useActionState } from 'react';
import { useFormStatus } from 'react-dom';

import { globalTokens as $, color, type } from '@/app/tokens.stylex';

import Calendar from './calander';

import type { ContactFormState, EmailAction } from '@/app/_actions/send-email';
import type { Language } from '@/lib/constants';

const s = sx.create({
  visHidden: {
    borderWidth: '0',
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: '1px',
    margin: '0 -1px -1px 0',
    opacity: 0,
    overflow: 'hidden',
    padding: '0',
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px',
    wordWrap: 'normal',
  },
  form: {
    borderColor: `rgb(${color.border})`,
    borderRadius: `calc(${$.space} * 4)`,
    borderStyle: 'solid',
    borderWidth: '1px',
    display: 'grid',
    gridColumnStart: 'main',
    marginBottom: `calc(${$.space} * 12)`,
    paddingBlock: `calc(${$.space} * 4 - 1px)`,
    paddingInline: `calc(${$.space} * 6)`,
    rowGap: `calc(${$.space} * 5)`,
  },
  msg: {
    backgroundColor: 'rgba(var(--msg-color),0.1)',
    borderColor: 'rgb(var(--msg-color))',
    borderRadius: $.space,
    borderStyle: 'solid',
    borderWidth: '1px',
    maxHeight: '0',
    opacity: 0,
    overflow: 'hidden',
    transition: 'opacity 2s ease-in-out, max-height 0.5s ease-in-out',
    visibility: 'hidden',
  },
  msgError: {
    // eslint-disable-next-line @stylexjs/valid-styles
    '--msgColor': color.accentB,
    maxHeight: `calc(${$.space} * 20)`,
    opacity: 1,
    visibility: 'visible',
  },
  msgSuccess: {
    // eslint-disable-next-line @stylexjs/valid-styles
    '--msgColor': color.accentA,
    display: 'block',
    maxHeight: `calc(${$.space} * 20)`,
    opacity: 1,
    visibility: 'visible',
  },
  msgInner: {
    padding: `calc(${$.space} * 4 - 1px)`,
  },
  inputWrapper: {
    display: 'grid',
    rowGap: $.space,
  },
  label: {
    display: 'grid',
    // eslint-disable-next-line @stylexjs/valid-styles
    fontWeight: '700',
  },
  required: {
    color: `rgb(${color.accentB})`,
    fontSize: type.fzMin2,
  },
  note: {
    color: `rgb(${color.textDim})`,
    fontSize: type.fzMin2,
    lineHeight: type.lhMin2,
  },
  input: {
    backgroundColor: {
      default: 'transparent',
      ':hover': `rgb(${color.surface0})`,
      ':focus': `rgb(${color.surface0})`,
    },
    borderColor: {
      default: `rgb(${color.borderDim})`,
      ':hover': `rgb(${color.border})`,
      ':focus': `rgb(${color.border})`,
    },
    borderRadius: $.space,
    borderStyle: 'solid',
    borderWidth: '1px',
    outline: {
      default: null,
      ':focus': '0',
    },
    paddingBlock: `calc(${$.space} * 1.5 - 1px)`,
    paddingInline: `calc(${$.space} * 2)`,
    transition: 'all 0.25s ease-in-out',
    width: '100%',

    '::placeholder': {
      color: `rgb(${color.textDim})`,
    },
  },
  textArea: {
    height: 'calc(8lh + 1ex)',
  },
  button: {
    borderColor: {
      default: `rgba(${color.accentA},0.3)`,
      ':hover': `rgb(${color.accentA})`,
      ':focus': `rgb(${color.accentA})`,
    },
    borderRadius: `calc(${$.space} * 2)`,
    borderStyle: 'solid',
    borderWidth: '2px',
    color: `rgb(${color.accentA})`,
    cursor: 'pointer',
    justifySelf: 'start',
    overflow: 'hidden',
    paddingBlock: `calc(${$.space} - 2px)`,
    paddingInline: `calc(${$.space} * 6)`,
    position: 'relative',
    textTransform: 'uppercase',
    transform: {
      default: 'scale(1)',
      ':active': 'scale(0.95)',
    },
    transition: 'all 0.25s ease-in-out',

    '::before': {
      animationDuration: '3.5s',
      animationIterationCount: 'infinite',
      animationTimingFunction: 'ease-in-out',
      backgroundColor: `rgba(${color.accentA}, 0.3)`,
      borderRadius: `calc(${$.space} * 1)`,
      content: '""',
      height: '100%',
      left: 0,
      position: 'absolute',
      top: 0,
      transform: 'translateX(-100%)',
      transformOrigin: '0 0',
      width: '100%',
    },
  },
  buttonPending: {
    cursor: 'not-allowed',
    opacity: 0.3,

    '::before': {
      animationName: sx.keyframes({
        '0%': { transform: 'translateX(-100%)' },
        '50%': { transform: 'translateX(100%)' },
        // eslint-disable-next-line @stylexjs/sort-keys
        '100%': { transform: 'translateX(-100%)' },
      }),
    },
  },
});

interface Props {
  lang: Language;
  arrivalLabel: string;
  arrivalNote: string;
  contactDetailsLabel: string;
  contactDetailsPlaceholder: string;
  contactDetailsNote: string;
  durationLabel: string;
  durationPlaceholder: string;
  durationNote: string;
  messageLabel: string;
  messageNote: string;
  countryLabel: string;
  countryNote: string;
  submit: string;
  sendEmailAction: EmailAction;
}

const initialFormData: ContactFormState = {
  message: '',
  error: false,
  success: false,
  fieldValues: {
    arrivalDay: '',
    arrivalMonth: '',
    arrivalYear: '',
    contact_details: '',
    country: '',
    duration: '',
    message: '',
  },
};

export default function ContactForm({
  lang,
  arrivalLabel,
  arrivalNote,
  contactDetailsLabel,
  contactDetailsNote,
  contactDetailsPlaceholder,
  countryLabel,
  countryNote,
  durationLabel,
  durationNote,
  durationPlaceholder,
  messageLabel,
  messageNote,
  submit,
  sendEmailAction,
}: Props) {
  const formRef = React.useRef<HTMLFormElement>(null);
  const successMsgRef = React.useRef<HTMLDivElement>(null);

  const [formState, formAction] = useActionState(sendEmailAction, initialFormData);
  const { pending } = useFormStatus();

  const [status, setStatus] = React.useState({
    error: formState.error,
    success: formState.success,
  });

  React.useEffect(() => {
    if (formState?.success) {
      formRef.current?.reset();
    }
    setStatus(() => ({
      error: formState.error,
      success: formState.success,
    }));
  }, [formState]);

  const isError = status.error && !pending;
  const isSuccess = status.success && !pending;

  if (isSuccess) {
    setTimeout(() => {
      successMsgRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 250);
  }

  return (
    <>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js"
        strategy="afterInteractive"
      />
      <form id="contactForm" action={formAction} ref={formRef} {...sx.props(s.form)}>
        <div {...sx.props(s.inputWrapper)}>
          <label htmlFor="contact_details" {...sx.props(s.label)}>
            <span>
              {contactDetailsLabel}
              <sup aria-hidden {...sx.props(s.required)}>
                *
              </sup>
              <span {...sx.props(s.visHidden)}> (required)</span>
            </span>
            <input
              {...sx.props(s.input)}
              type={lang === 'en' ? 'email' : 'text'}
              id="contact_details"
              name="contact_details"
              placeholder={contactDetailsPlaceholder}
              aria-describedby="contactDetailsNote"
              required
            />
          </label>
          <span id="contactDetailsNote" {...sx.props(s.note)}>
            {contactDetailsNote}
          </span>
        </div>

        <div {...sx.props(s.inputWrapper)}>
          <label htmlFor="country" {...sx.props(s.label)}>
            <span>{countryLabel}</span>
            <input
              {...sx.props(s.input)}
              type="text"
              id="country"
              name="country"
              aria-describedby="countryNote"
            />
          </label>
          <span id="countryNote" {...sx.props(s.note)}>
            {countryNote}
          </span>
        </div>

        <div {...sx.props(s.inputWrapper)}>
          <label htmlFor="message" {...sx.props(s.label)}>
            <span>
              {messageLabel}
              <sup aria-hidden {...sx.props(s.required)}>
                *
              </sup>
              <span {...sx.props(s.visHidden)}> (required)</span>
            </span>
            <textarea
              {...sx.props(s.input, s.textArea)}
              id="message"
              name="message"
              aria-describedby="messageNote"
              required
            />
          </label>
          <span id="messageNote" {...sx.props(s.note)}>
            {messageNote}
          </span>
        </div>

        <div {...sx.props(s.inputWrapper)}>
          <h4 {...sx.props(s.label)}>{arrivalLabel}</h4>
          <p id="arrivalNote" {...sx.props(s.note)}>
            {arrivalNote}
          </p>
          <Calendar lang={lang} />
        </div>

        <div {...sx.props(s.inputWrapper)}>
          <label htmlFor="duration" {...sx.props(s.label)}>
            <span>{durationLabel}</span>
            <input
              {...sx.props(s.input)}
              type="text"
              id="duration"
              name="duration"
              aria-describedby="durationNote"
              placeholder={durationPlaceholder}
            />
          </label>
          <span id="durationNote" {...sx.props(s.note)}>
            {durationNote}
          </span>
        </div>

        <div
          suppressHydrationWarning
          className="cf-turnstile"
          data-sitekey={process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY}
          data-action="contact-form"
          data-theme="light"
          data-lang={lang}
          data-appearance="interaction-only"
        />
        <div
          aria-live={isError ? 'assertive' : 'polite'}
          role={isError ? 'alert' : 'status'}
          {...sx.props(s.msg, isError && s.msgError, isSuccess && s.msgSuccess)}
        >
          {isError || isSuccess ? <div {...sx.props(s.msgInner)}>{formState.message}</div> : null}
        </div>
        <button
          type="submit"
          {...sx.props(s.button, pending && s.buttonPending)}
          disabled={pending}
        >
          {submit}
        </button>
      </form>
    </>
  );
}
